import React, { useState, useRef } from 'react';
import { graphql, navigate } from 'gatsby';
import { Button, TextField, Password } from '@lesmills-international/components';
import styled from 'styled-components';
import { Auth } from "@aws-amplify/auth";
import Box from '@mui/material/Box';
import { RichText } from "prismic-reactjs";
import { linkResolver } from '../../../../src/services/linkResolver';
import Layout from '../components/common/layout';
import Title from '../components/common/title';
import Alert from '../components/common/alert';

import { Subtitle, Wrapper } from '../components/signup/createAccount/style';
import RecaptchaV3 from '../components/common/recaptchaV3';
import { checkHaveErrors, formValidator } from '../utils/validator';
import { addErrorInDatadogRum, setLocalStorage, getLocalStorage } from '../utils/utilities';
import ROUTES from '../../../../src/constants/routes';

const Form = styled(Box)(() => ({
  '& .MuiTextField-root': { marginBottom: '15px', width: '320px' },
  maxWidth: '320px',
  textAlign: 'center',

  '& .MuiInputBase-input:focus': { boxShadow: 'none' },
  '& a': { textDecoration: 'underline' }
}));
const FORGOT_PWD_KEY = "forgot-pwd";
const USER_INFO_KEY = "user-info";

interface IError {
  [key: string]: {
    errorMsg: string;
    valid: boolean;
  }
}

const ResetPasswordPage = ({ pageContext, data }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState<IError>({});
  const [isSuccess, setIsSuccess] = useState(false);
  const codeRef = useRef(null);
  const passwordRef = useRef(null);
  const recaptchaRef = useRef();
  const { lang } = pageContext;

  const { prismicSignUpLayout, prismicResetPasswordPage: { data: rpData } } = data;

  const getValue = (current: { value: string; }) => current.value;

  const getValidatorResult = (code: string, password: string) => ({
    code: formValidator(["REQUIRED", "CODE"], code, {
      requiredError: rpData.required_code.text,
      invalidError: rpData.invalid_code.text,
    }),
    password: formValidator(["REQUIRED", "PASSWORD"], password, {
      requiredError: rpData.required_new_password.text,
      invalidError: rpData.invalid_password.text,
    }),
  });

  const handleSubmit = (e: { which?: number; preventDefault: any; }) => {
    e.preventDefault();
    const code = codeRef.current ? getValue(codeRef.current).trim() : ""
    const password = passwordRef.current
      ? getValue(passwordRef.current).trim()
      : "";
    
    const validatorErrors = getValidatorResult(code, password);

    setErrors(validatorErrors);

    // Get forgot pwd email from localstorage
    const forgotPwd = JSON.parse(getLocalStorage(FORGOT_PWD_KEY) || "{}");
    const email = forgotPwd && forgotPwd.email;

    // if no error
    if (!checkHaveErrors(validatorErrors) && email) {
      handleResetPassword(email, code, password)
    }
  };

  const handleResetPassword = (email: string, code: string, password: string) => {
    setIsProcessing(true)

    // Integrate with API
    Auth.forgotPasswordSubmit(email, code, password)
      .then(() => {
        const userInfo = JSON.parse(getLocalStorage(USER_INFO_KEY) || "{}");
        setLocalStorage(FORGOT_PWD_KEY, JSON.stringify({ success: true }));
        if (Object.keys(userInfo).length > 0) {
          // back to /account-details if logged in
          navigate(ROUTES(lang).ACCOUNT_DETAILS)
          return
        }
        // Show success message and redirect after 2 seconds
        setIsSuccess(true);
        setTimeout(() => {
          navigate(ROUTES(lang).LOGIN);
        }, 5000);
      })
      .catch(err => {
        let codeError;
        switch(err.code) {
          case 'ExpiredCodeException':
            codeError = err.message;
            break;
          case 'CodeMismatchException':
            codeError = rpData.code_mismatch.text;
            break;
          default:
            codeError = 'Unknown error happened, please try again';
        }
        setErrors({
          code: { errorMsg: codeError, valid: false },
          password: { errorMsg: err.code === "InvalidParameterException"
              ? rpData.invalid_password.text
              : "", valid: false }
        });
        addErrorInDatadogRum(err);
      })
      .finally(() => setIsProcessing(false))
  }

  const handleOnKeyPress = (e: { which: number; preventDefault: () => void; }) => {
    if (e.which === 13) {
      e.preventDefault();
      handleSubmit(e);
    }
  }

  return (
    <Layout
      data={prismicSignUpLayout.data}
      lang={lang}
      pathName="/reset-password"
      omitClose
      scripts={[]}
    >
      <Wrapper>
        <Title
          id="reset-password-title"
          variant='h5'
          style={{ marginTop: '24px', fontSize: '28px' }}
        >
          {rpData.title.text}
        </Title>
        <Subtitle>{rpData.subtitle.raw[0].text}</Subtitle>
        <Subtitle>{rpData.subtitle.raw[1].text}</Subtitle>
        {isSuccess && (
          <Alert
            severity="success"
            testId="reset-password-success-alert"
            style={{ marginBottom: '20px' }}
          >
            <RichText render={rpData.reset_password_success_text.raw} linkResolver={linkResolver} />
          </Alert>
        )}
        <Form id="reset-pwd-form" component="form" noValidate>
          { TextField && <TextField
            label={rpData.reset_code_label.text}
            error={!!errors.code?.errorMsg}
            inputRef={codeRef}
            onKeyUp={handleOnKeyPress}
            data-testid="reset-pwd-code"
            id="reset-pwd-code"
            disabled={isProcessing}
            helperText={errors.code?.errorMsg}
          /> }
          { Password && <Password
            label={rpData.new_password_label.text}
            error={!!errors.password?.errorMsg}
            inputRef={passwordRef}
            onKeyUp={handleOnKeyPress}
            data-testid="reset-pwd-password"
            id="password"
            disabled={isProcessing}
            helperText={errors.password?.errorMsg}
          /> }

          { Button && <Button
            type="submit"
            id="submit-button"
            onClick={handleSubmit}
            disabled={isProcessing}
            submitting={isProcessing}
            style={{ width: '100%', marginTop: '20px' }}
            data-testid="reset-pwd-btn"
            variant="contained"
            label={rpData.reset_password_button_label.text}
          /> }
          <div
            className={isProcessing ? " disabled" : ""}
            data-cy="resend-code-text"
            style={{ marginTop: '20px' }}
          >
          <RichText render={rpData.resend_code_text.raw} linkResolver={linkResolver} />
          </div>
        </Form>
        <RecaptchaV3 ref={recaptchaRef} />
      </Wrapper>
    </Layout>
  );
};

export default ResetPasswordPage;

export const query = graphql`
  query ($lang: String) {
    prismicSignUpLayout(lang: { eq: $lang }) {
      _previewable
      data {
        logo_white {
          alt
          gatsbyImageData(width: 133, height: 33)
        }
        logo_black {
          alt
          gatsbyImageData(width: 133, height: 33)
        }
        background_image {
          alt
          gatsbyImageData
        }
        body {
          ... on PrismicSignUpLayoutDataBodyFooterNavigation {
            items {
              nav_label {
                text
              }
              nav_link {
                text
              }
            }
          }
        }
        body1 {
          ... on PrismicSignUpLayoutDataBody1Countries {
            items {
              country_code {
                text
              }
              country_name {
                text
              }
            }
          }
        }
        body2 {
          ... on PrismicSignUpLayoutDataBody2States {
            primary {
              country_name {
                text
              }
              country_code {
                text
              }
            }
            items {
              state_name {
                text
              }
              state_code {
                text
              }
              state_short_name {
                text
              }
            }
          }
        }
      }
    }
    prismicResetPasswordPage(lang: { eq: $lang }) {
      _previewable
      data {
        subtitle {
          raw
        }
        code_mismatch {
          text
        }
        invalid_code {
          text
        }
        invalid_password {
          text
        }
        new_password_label {
          text
        }
        page_author {
          text
        }
        page_description {
          text
        }
        page_image {
          alt
          url
        }
        page_keywords {
          text
        }
        page_og_description {
          text
        }
        page_og_type {
          text
        }
        page_og_title {
          text
        }
        page_title {
          text
        }
        page_twitter_card {
          text
        }
        page_twitter_description {
          text
        }
        page_twitter_title {
          text
        }
        required_code {
          text
        }
        required_new_password {
          text
        }
        resend_code_text {
          raw
        }
        reset_code_label {
          text
        }
        reset_password_button_label {
          text
        }
        reset_password_success_text {
          raw
        }
        title {
          text
        }
      }
    }
  }
`;
